/**
 * PF Revamp — root Zustand store.
 *
 * Combines 8 slices. Uses:
 *   - devtools middleware (Redux DevTools integration — named 'pf-revamp')
 *   - subscribeWithSelector middleware (Module 3a will use this to attach
 *     the auto-save subscription)
 *
 * NO persist middleware — the draft auto-save (local + BE) is a deliberate
 * subscription, not an automatic middleware hook (see master plan §4).
 */

import { create, type StateCreator } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'

import { type BillingSlice, createBillingSlice } from './slices/billingSlice'
import { type CartAttachmentsSlice, createCartAttachmentsSlice } from './slices/cartAttachmentsSlice'
import { type CartSlice, createCartSlice } from './slices/cartSlice'
import { type CatalogueSlice, createCatalogueSlice } from './slices/catalogueSlice'
import { type DraftSlice, createDraftSlice } from './slices/draftSlice'
import { type InstructionsSlice, createInstructionsSlice } from './slices/instructionsSlice'
import { type ProductConfigSlice, createProductConfigSlice } from './slices/productConfigSlice'
import { type ProductDrawerSlice, createProductDrawerSlice } from './slices/productDrawerSlice'
import { type PropertySlice, createPropertySlice } from './slices/propertySlice'
import { type StepNavSlice, createStepNavSlice } from './slices/stepNavSlice'

export type PfStoreState =
  & CartSlice
  & CartAttachmentsSlice
  & CatalogueSlice
  & PropertySlice
  & StepNavSlice
  & InstructionsSlice
  & BillingSlice
  & DraftSlice
  & ProductConfigSlice
  & ProductDrawerSlice

/** Kept when an order starts fresh: chosen once per visit, or registered by a mounted component. */
export const KEPT_ON_RESET: ReadonlySet<string> = new Set(['entryVariant', 'placeOrderHandler', 'stripeConfirmHandler'])

const combineSlices: StateCreator<PfStoreState, [], [['zustand/subscribeWithSelector', never]]> =
  subscribeWithSelector((...a) => {
    const slices = {
      ...createCartSlice(...a),
      ...createCartAttachmentsSlice(...a),
      ...createCatalogueSlice(...a),
      ...createPropertySlice(...a),
      ...createStepNavSlice(...a),
      ...createInstructionsSlice(...a),
      ...createBillingSlice(...a),
      ...createDraftSlice(...a),
      ...createProductConfigSlice(...a),
      ...createProductDrawerSlice(...a),
    }
    // A fresh order must carry nothing of the previous one, so reset restores every field the store
    // was created with rather than a hand-kept list that misses whatever is added next.
    const fresh = Object.fromEntries(
      Object.entries(slices).filter(([key, value]) => typeof value !== 'function' && !KEPT_ON_RESET.has(key))
    ) as Partial<PfStoreState>
    return { ...slices, resetPersistedState: () => a[0](fresh) }
  })

// `devtools` is applied DEV-only, so the middleware mutator
// chain is path-dependent: the bare path carries only `subscribeWithSelector`,
// while the devtools path additionally carries `devtools`. A single static type
// for `combineSlices` cannot satisfy both `create()` calls, so we re-assert the
// devtools-path mutators here. Runtime is unchanged — this only re-labels the
// initializer's mutator chain for the conditionally-applied middleware.
const combineSlicesWithDevtools = combineSlices as StateCreator<
  PfStoreState,
  [['zustand/devtools', never]],
  [['zustand/subscribeWithSelector', never]]
>

export const usePfStore = import.meta.env.DEV
  ? create<PfStoreState>()(devtools(combineSlicesWithDevtools, { name: 'pf-revamp' }))
  : create<PfStoreState>()(combineSlices)

/**
 * Dev / Cypress only: expose the store on `window` so E2E tests can seed
 * state (cart items, property, billing) directly without driving the full
 * UI flow. Production builds are tree-shaken by the `import.meta.env.PROD`
 * check — Vite statically replaces the value at build time.
 */
if (!import.meta.env.PROD && typeof window !== 'undefined') {
  (window as unknown as { __pfStore?: typeof usePfStore }).__pfStore = usePfStore
}
